import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './../../context/AuthContext';
import visorService from './../../services/VisorService';
import getBaseUrl from './../../utils/baseUrl';
import { useFlow } from './../../context/FlowContext';
import onePng from './../../assets/images/one-40.png';
import greenTick from './../../assets/images/green-tick.png';
import useAndroidBody  from './../hooks/useAndroidBody.js';
import DownloadButton from './../Buttons/DownloadButton.js';

const fetchGuideHtml = async (fileName) => {
  const res = await fetch(`${getBaseUrl()}/guides/android/${fileName}`);
  if (!res.ok) throw new Error(`Failed to fetch ${fileName}`);
  const text = await res.text();
  return DOMPurify.sanitize(text);
};

export default function VisorID() {
  useAndroidBody();
  const nav = useNavigate();
  const { constabulary, forcePrefix, androidApk } = useAuth();
  const { pnc, setPnc, visorId, setVisorId, suffix, setSuffix, created, setCreated, finalVisorId, setFinalVisorId } = useFlow();
  const [para1, setPara1] = useState('');
  const [loading, setLoading] = useState(true);
  const [pncErr, setPncErr] = useState('');
  const [visorErr, setVisorErr] = useState('');
  const [suffixErr, setSuffixErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const p1 = await fetchGuideHtml('android3.html');
        setPara1(p1);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  const [existingVisors, setExistingVisors] = useState([]);          
  const [existingVisorError, setExistingVisorError] = useState(''); 

  useEffect(() => {
    (async () => {
      try {
        const list = await visorService.getVisorIdList();  
        setExistingVisors(list || []);
      } catch (err) {
        setExistingVisorError('No inactive device IDs found. Please create a new one.');  
      }
    })();
  }, []); 

    const handleExistingVisorSelect = (e) => {                 
    const selected = e.target.value;
    if (!selected) {
      setFinalVisorId('');
      setCreated(false);
      return;
    }
    setFinalVisorId(selected);
    setCreated(true);
    setCreateError('');
  }; 

  const handlePncChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setPnc(value);
    setPncErr(value ? '' : 'PNC is required');
    setCreated(false);
  };
  const handleVisorChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase().slice(0, 13);
    setVisorId(value);
    setCreated(false);
    if (!value) setVisorErr('Device ID is required');
    else if (value.length < 3) setVisorErr('Device ID looks too short');
    else setVisorErr('');
  };
  const handleSuffixChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 2);
    setSuffix(value);
    setCreated(false);
    setSuffixErr('');
  };

  const previewVisor = useMemo(() => {
    const parts = [/*forcePrefix || 'PREFIX', */visorId || 'Identifier', suffix || '0'];
    return parts.join('-');
  }, [/*forcePrefix, */visorId, suffix]);

  const canCreate = useMemo(() => {
    return !creating && !created /*&& pnc*/ && visorId /*&& !pncErr*/ && !visorErr && !suffixErr;
  }, [creating, created, /*pnc,*/ visorId, pncErr, visorErr, suffixErr]);

  const createVisor = async () => {
    if (!canCreate) return;
    try {
      setCreating(true);
      setCreateError('');
      const newVisorId = `${visorId}-${suffix}`;//`${forcePrefix}-${visorId}-${suffix}`;
      await visorService.createVisor(newVisorId, pnc ? pnc : "0");
      setFinalVisorId(newVisorId);
      setCreated(true);
    } catch (e) {
      setCreateError(e?.message || 'Failed to create Device ID. Please try again.');
      setCreated(false);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="android-install ai-wrap">
      <header className="ai-header">
        <div className="ai-header__title">
          <span>eSafe for Android</span>
          {constabulary ? <small className="ai-header__sub">{constabulary}</small> : null}
        </div>
      </header>

      <main className="ai-main">
        <section className="card">
          <div className="step-chip">
            <img src={onePng} alt="Step 1" />
            <div className="step-chip__text">Create your Device reference</div>
          </div>

          {loading ? <div className="skeleton"><div className="skeleton__line"/></div>
                   : <div className="html-content" dangerouslySetInnerHTML={{ __html: para1 }} />}

          <div className="form">
            {/* <label className="label" htmlFor="pnc">PNC <span className="req">*</span></label>
            <input id="pnc" className="android-input" inputMode="numeric" maxLength={15} value={pnc} onChange={handlePncChange} />
            {pncErr && <div className="field-error" role="alert">{pncErr}</div>} */}

            <div className="group-row">
              {/* <div className="group-col">
                <label className="label" htmlFor="prefix">Prefix</label>
                <input id="prefix" className="android-input-prefix input--ro" readOnly value={forcePrefix || ''} />
              </div> */}

              {/* Existing Device ID dropdown */}
            <div className="group-row" style={{ marginBottom: 16 }}>
              <div className="group-col">
                <h3 className="label" htmlFor="existingVisor">
                  Already created the Device ID?
                </h3>
                <select
                  id="existingVisor"
                  className="android-input"
                  value={finalVisorId || ''}
                  onChange={handleExistingVisorSelect}
                >
                  <option value="">
                    If yes, click here to select it
                  </option>
                  {existingVisors.map(v => (
                    <option key={v.id} value={v.username}>
                      {v.username}
                    </option>
                  ))}
                </select>
                {existingVisorError && (
                  <div className="field-error" role="alert">
                    {existingVisorError}
                  </div>
                )}
              </div>
            </div>
            {/* END Existing */}

              <div className="group-col">
                <label className="label" htmlFor="visorId">Device ID <span className="req">*</span></label>
                <input  id="visorId" 
                        className="android-input" 
                        value={visorId} 
                        onChange={handleVisorChange} 
                        placeholder="ABC123456" />
                        
                {visorErr && <div className="field-error" role="alert">{visorErr}</div>}
              </div>
              <div className="group-col group-col--tiny">
                <label className="label" htmlFor="suffix">Suffix</label>
                <input id="suffix" className="android-input" maxLength={2} value={suffix} onChange={handleSuffixChange} placeholder="0" />
                {suffixErr && <div className="field-error" role="alert">{suffixErr}</div>}
              </div>
            </div>

            <div className="preview">
              <div className="preview__label">Your Device ID will be:</div>
              <div className="preview__value" aria-live="polite">{previewVisor}</div>
            </div>

            <button className="btn btn--primary" onClick={createVisor} disabled={!canCreate}>
              {creating ? 'Creating…' : created ? (<><img src={greenTick} alt="" className="icon-inline" /> Created</>) : 'Create Device ID'}
            </button>

            {createError && <div className="field-error" role="alert">{createError}</div>}
            {created && (
              <div className="success">
                <div className="success__body">
                  <div className="success__title">Device ID created</div>

                  <div className="success__row">
                    <div className="success__id">{finalVisorId}</div>
                    <button
                      className="btn btn--secondary success__copy"
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(finalVisorId.trim());
                          const btn = document.activeElement;
                          const originalText = btn.textContent;
                          btn.textContent = 'Copied!';
                          setTimeout(() => {
                            btn.textContent = originalText;
                          }, 1200);
                        } catch {
                          alert('Copy failed. Please copy manually.');
                        }
                      }}
                    >
                      Copy
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="card card--muted" style={{ marginTop: 16 }}>
            <h3>Is the device running Android 14 or above?</h3>
            <div className="btn-row">
              <button className="btn btn--accent" onClick={() => nav('/android/instover14')}>Yes</button>
              <button className="btn btn--ghost" onClick={() => nav('/android/inunder14')}>No</button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
